import {
  allowedRates,
  b2clThreshold,
  CREDIT_NOTE_REASONS,
  checkGstin,
  DEBIT_NOTE_REASONS,
  formatPaise,
  GST_STATES,
  invoiceNumberProblem,
  isServiceCode,
  lineTax,
  type NoteReason,
  readRupees,
  tidyInvoiceNumber,
} from '@oneapp/gst';
import type { Invoice, OriginalInvoice, ReturnDraft, SaleInput } from '@oneapp/shared';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShortDate } from '../business/parts';
import { Icon } from '../components/icons';
import { Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { salesApi, salesKeys } from './api';
import { defaultDate, HsnField, indiaToday, SelectRow, TextRow } from './SaleForm';

/**
 * K2 · A credit or debit note (gap 4): the invoice it's for (one of the business's, found by number or
 * buyer, or one from before EkApp, typed), the note's own number and date, why, and what it takes back
 * (or adds) on the invoice's item, at the invoice's rate and place. The tax is worked out as it's typed;
 * the server's checks (the 30 November limit, what's left on the invoice) are what count.
 */

type DocType = 'credit' | 'debit';

/** An invoice from before EkApp, as typed. */
interface Typed {
  number: string;
  date: string;
  gstin: string;
  consumer: boolean;
  /** A consumer's state (place of supply). */
  state: string;
  /** A consumer bill to another state over ₹1 lakh: its note is listed on its own. */
  large: boolean;
}

interface Problems {
  original?: string;
  number?: string;
  date?: string;
  hsn?: string;
  rate?: string;
  value?: string;
  quantity?: string;
  other?: string;
}

export interface NoteFormProps {
  returnId: string;
  draft: ReturnDraft;
  sellerGstin: string;
  /** Changing this note; a new one without. */
  note?: Invoice;
  /** A new note for this invoice. */
  against?: string;
  docType?: DocType;
  formId: string;
  onBusy?: (busy: boolean) => void;
  onSaved: (result: { invoice: Invoice; draft: ReturnDraft }) => void;
  onDeleted?: (draft: ReturnDraft) => void;
}

const money = (paise: number) => formatPaise(paise);
const rupees = (paise: number) =>
  (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: paise % 100 ? 2 : 0 });
const firstReason = (docType: DocType): NoteReason => (docType === 'credit' ? 'returned' : 'price_up');
let keys = 0;

export function NoteForm({
  returnId,
  draft,
  sellerGstin,
  note,
  against,
  docType: startType,
  formId,
  onBusy,
  onSaved,
  onDeleted,
}: NoteFormProps) {
  const { t } = useTranslation();
  const shortDate = useShortDate();
  const seller = sellerGstin.slice(0, 2);
  const state = (code: string) => GST_STATES[code] ?? code;
  const original = note?.against[0] ?? null;
  const originalId = note ? (original?.invoiceId ?? null) : (against ?? null);

  const [docType, setDocType] = useState<DocType>(note?.docType === 'debit' ? 'debit' : (startType ?? 'credit'));
  const [picked, setPicked] = useState<OriginalInvoice | null>(null);
  const [typed, setTyped] = useState<Typed | null>(
    note && original && !original.invoiceId
      ? {
          number: original.number,
          date: original.date,
          gstin: note.buyer?.gstin ?? '',
          consumer: !note.buyer,
          state: note.placeOfSupply,
          large: note.kind === 'b2cl',
        }
      : null,
  );
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [number, setNumber] = useState(note?.numbers[0] ?? '');
  const [numberTouched, setNumberTouched] = useState(Boolean(note));
  const [date, setDate] = useState(note?.date ?? defaultDate(draft.period));
  const [reason, setReason] = useState<NoteReason>(note?.reason ?? firstReason(docType));
  const [lineIndex, setLineIndex] = useState(0);
  const line0 = note?.lines[0];
  const [hsn, setHsn] = useState(line0?.hsn ?? '');
  const [hsnName, setHsnName] = useState(line0?.description ?? '');
  const [rate, setRate] = useState(line0 ? String(line0.rate) : '');
  const [quantity, setQuantity] = useState(line0 ? String(line0.quantity) : '');
  const [value, setValue] = useState(line0 ? rupees(line0.taxableValue) : '');
  const [problems, setProblems] = useState<Problems>({});
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [key] = useState(() => `note-${Date.now()}-${++keys}`);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  /** Starts from an invoice: its item (the first taxed one), at its rate. */
  const choose = useCallback(
    (invoice: OriginalInvoice, fill: boolean) => {
      setPicked(invoice);
      setTyped(null);
      setProblems({});
      const lines = invoice.lines.filter((l) => l.rate > 0);
      const line = lines[0] ?? invoice.lines[0];
      setLineIndex(0);
      if (fill && line) {
        setHsn(line.hsn);
        setHsnName(line.description ?? '');
        setRate(String(line.rate));
        setQuantity(reason === 'returned' && !isServiceCode(line.hsn) ? '1' : '0');
      }
    },
    [reason],
  );

  // The invoice it's for, when known: from the chat, or the note being changed. Picked once, so "Change"
  // can pick another; a search with nothing typed shares its cache key, so it's matched by id.
  const known = useQuery({
    queryKey: salesKeys.originals(draft.businessId, '', originalId ?? ''),
    queryFn: ({ signal }) => salesApi.originals(draft.businessId, { id: originalId ?? '' }, signal),
    enabled: Boolean(originalId),
    staleTime: 0,
  });
  const knownInvoice = originalId ? (known.data?.invoices.find((i) => i.id === originalId) ?? null) : null;
  const [knownUsed, setKnownUsed] = useState(false);
  const fillFromKnown = !note;
  useEffect(() => {
    if (!knownInvoice || knownUsed) return;
    setKnownUsed(true);
    choose(knownInvoice, fillFromKnown);
  }, [knownInvoice, knownUsed, fillFromKnown, choose]);
  const searching = !originalId || knownUsed || (known.isSuccess && !knownInvoice);
  const found = useQuery({
    queryKey: salesKeys.originals(draft.businessId, debounced, ''),
    queryFn: ({ signal }) => salesApi.originals(draft.businessId, { q: debounced }, signal),
    enabled: !picked && !typed && searching,
  });
  const next = useQuery({
    queryKey: salesKeys.nextNote(returnId, docType, date),
    queryFn: ({ signal }) => salesApi.nextNoteNumber(returnId, docType, date, signal),
    enabled: !note,
  });
  useEffect(() => {
    if (!numberTouched && next.data) setNumber(next.data.number);
  }, [next.data, numberTouched]);

  const taxedLines = picked ? picked.lines.filter((l) => l.rate > 0) : [];
  const inter = picked
    ? picked.interState
    : typed
      ? typed.consumer
        ? typed.state !== seller
        : typed.gstin.slice(0, 2) !== seller
      : false;
  const rates = picked
    ? [...new Set(taxedLines.map((l) => l.rate))]
    : typed && /^\d{4}-\d{2}-\d{2}$/.test(typed.date)
      ? allowedRates(typed.date, isServiceCode(hsn) ? 'services' : 'goods').filter((r) => r > 0)
      : [];
  const taxable = readRupees(value);
  const rateNumber = rate === '' ? null : Number(rate);
  const tax = taxable !== null && rateNumber !== null ? lineTax(taxable, rateNumber, inter) : null;
  const kind = picked?.kind ?? (typed ? (typed.consumer ? (typed.large && inter ? 'b2cl' : 'b2cs') : 'b2b') : null);
  const reasons = docType === 'credit' ? CREDIT_NOTE_REASONS : DEBIT_NOTE_REASONS;

  const setType = (next: DocType) => {
    setDocType(next);
    setReason(firstReason(next));
    if (next === 'debit') setQuantity('0');
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    const found: Problems = {};
    if (!picked && !typed) found.original = t('sales.note.errors.original');
    if (typed) {
      if (!typed.number.trim()) found.original = t('sales.note.errors.originalNumber');
      else if (!/^\d{4}-\d{2}-\d{2}$/.test(typed.date)) found.original = t('sales.note.errors.originalDate');
      else if (!typed.consumer && !checkGstin(typed.gstin).ok) found.original = t('sales.form.errors.gstin');
    }
    const tidy = tidyInvoiceNumber(number);
    const numberProblem = invoiceNumberProblem(tidy);
    if (numberProblem) found.number = t(`sales.form.errors.number.${numberProblem}`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) found.date = t('sales.form.errors.required');
    else if (date > indiaToday()) found.date = t('sales.form.errors.future');
    if (!/^\d{4}(\d{2}){0,2}$/.test(hsn)) found.hsn = t('sales.form.errors.hsn');
    if (rateNumber === null) found.rate = t('sales.form.errors.required');
    if (!taxable) found.value = t('sales.form.errors.amount');
    setProblems(found);
    if (Object.keys(found).length) return;

    const consumerState = typed?.consumer ? typed.state : null;
    const input: SaleInput = {
      docType,
      reason,
      against: [
        picked
          ? { invoiceId: picked.id, number: picked.number, date: picked.date }
          : { number: tidyInvoiceNumber(typed?.number ?? ''), date: typed?.date ?? '' },
      ],
      ...(typed?.consumer && typed.large && inter ? { originalValue: b2clThreshold(typed.date) + 1 } : {}),
      numbers: [tidy],
      date,
      buyerGstin: picked
        ? (picked.buyer?.gstin ?? null)
        : typed?.consumer
          ? null
          : (typed?.gstin.trim().toUpperCase() ?? null),
      ...(consumerState ? { placeOfSupply: consumerState } : {}),
      lines: [
        {
          hsn,
          ...(hsnName ? { description: hsnName.slice(0, 120) } : {}),
          quantity: Number(quantity) > 0 ? Number(quantity) : 0,
          rate: rateNumber as number,
          taxableValue: taxable as number,
        },
      ],
    };
    setBusy(true);
    onBusy?.(true);
    try {
      const result = note
        ? await salesApi.save(returnId, note.id, input, note.version)
        : await salesApi.add(returnId, input, key);
      onSaved({ invoice: result.invoice, draft: result.return });
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      const fields = error.problem?.fields ?? {};
      const code = error.problem?.code;
      const next: Problems = {};
      for (const [field, message] of Object.entries(fields)) {
        if (field.startsWith('numbers')) next.number = message;
        else if (field === 'date') next.date = message;
        else if (field.startsWith('against') || field === 'buyerGstin') next.original = message;
        else if (field.endsWith('.rate')) next.rate = message;
        else if (field.endsWith('.hsn')) next.hsn = message;
        else if (field.startsWith('lines')) next.value = message;
      }
      if (code === 'INVOICE_NUMBER_TAKEN') next.number = error.problem?.detail ?? t('sales.form.errors.taken');
      else if (!Object.keys(next).length || code === 'NOTE_TOO_LATE' || fields.lines)
        next.other = error.problem?.detail ?? t('sales.note.failed');
      setProblems(next);
    } finally {
      setBusy(false);
      onBusy?.(false);
    }
  }

  async function remove() {
    if (!note) return;
    setBusy(true);
    try {
      onDeleted?.((await salesApi.remove(returnId, note.id, note.version)).return);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setProblems({ other: error.problem?.detail ?? t('sales.note.failed') });
    } finally {
      setBusy(false);
    }
  }

  const deadline = picked && docType === 'credit' ? picked.creditNoteDeadline : null;
  return (
    <form id={formId} className="sale-form note-form" noValidate onSubmit={submit}>
      <h2 className="sale-form__title">
        {(picked ?? typed)?.number
          ? t(docType === 'credit' ? 'sales.note.creditFor' : 'sales.note.debitFor', {
              number: (picked ?? typed)?.number,
            })
          : t('sales.note.title')}
      </h2>
      <fieldset className="filter-tabs" aria-label={t('sales.note.type')}>
        {(['credit', 'debit'] as const).map((type) => (
          <button
            key={type}
            type="button"
            className="filter-tabs__option"
            aria-pressed={docType === type}
            onClick={() => setType(type)}
            disabled={Boolean(note)}
          >
            {t(`sales.note.${type}`)}
          </button>
        ))}
      </fieldset>

      {picked ? (
        <div className="note-form__original">
          <Icon name="receipt" size={20} />
          <span className="note-form__original-text">
            <strong>
              {picked.number} · {shortDate(picked.date)}
            </strong>
            <span className="muted">
              {picked.buyer
                ? `${picked.buyer.name ?? picked.buyer.gstin} · ${picked.buyer.gstin}`
                : t('sales.list.consumer')}
              {' · '}
              {money(picked.invoiceValue)}
            </span>
          </span>
          {!note ? (
            <button type="button" className="linkbtn" onClick={() => setPicked(null)}>
              {t('sales.note.change')}
            </button>
          ) : null}
        </div>
      ) : typed ? (
        <fieldset className="note-form__typed">
          <legend className="field__label">{t('sales.note.before')}</legend>
          <div className="fields-row">
            <TextRow
              label={t('sales.note.invoiceNumber')}
              value={typed.number}
              onChange={(v) => setTyped({ ...typed, number: v.toUpperCase() })}
              maxLength={16}
            />
            <TextRow
              label={t('sales.note.invoiceDate')}
              type="date"
              value={typed.date}
              max={indiaToday()}
              onChange={(v) => setTyped({ ...typed, date: v })}
            />
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              checked={typed.consumer}
              onChange={(e) => setTyped({ ...typed, consumer: e.target.checked })}
            />
            <span>{t('sales.note.toConsumer')}</span>
          </label>
          {typed.consumer ? (
            <>
              <SelectRow
                label={t('sales.form.placeOfSupply')}
                value={typed.state}
                onChange={(v) => setTyped({ ...typed, state: v })}
                options={Object.entries(GST_STATES).map(([code, name]) => ({ value: code, label: name }))}
              />
              {typed.state !== seller ? (
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={typed.large}
                    onChange={(e) => setTyped({ ...typed, large: e.target.checked })}
                  />
                  <span>{t('sales.note.large')}</span>
                </label>
              ) : null}
            </>
          ) : (
            <TextRow
              label={t('sales.form.gstin')}
              value={typed.gstin}
              onChange={(v) => setTyped({ ...typed, gstin: v.toUpperCase() })}
              maxLength={15}
            />
          )}
          {!note ? (
            <button type="button" className="linkbtn" onClick={() => setTyped(null)}>
              {t('sales.note.findInstead')}
            </button>
          ) : null}
        </fieldset>
      ) : (
        <div className="note-form__find">
          <TextRow
            label={t('sales.note.find')}
            value={search}
            onChange={setSearch}
            placeholder={t('sales.note.findPlaceholder')}
            error={problems.original}
          />
          <ul className="note-form__results" aria-label={t('sales.note.results')}>
            {(found.data?.invoices ?? []).map((invoice) => (
              <li key={invoice.id}>
                <button type="button" className="sales__row" onClick={() => choose(invoice, true)}>
                  <span className="sales__text">
                    <span className="sales__number">{invoice.number}</span>
                    <span className="sales__meta">
                      {invoice.buyer?.name ?? invoice.buyer?.gstin ?? t('sales.list.consumer')} ·{' '}
                      {shortDate(invoice.date)}
                    </span>
                  </span>
                  <span className="sales__amount">{money(invoice.invoiceValue)}</span>
                </button>
              </li>
            ))}
            {found.data && !found.data.invoices.length ? <li className="muted">{t('sales.note.noneFound')}</li> : null}
          </ul>
          <button
            type="button"
            className="linkbtn"
            onClick={() =>
              setTyped({
                number: search.trim().toUpperCase(),
                date: '',
                gstin: '',
                consumer: false,
                state: seller,
                large: false,
              })
            }
          >
            {t('sales.note.typeOne')}
          </button>
        </div>
      )}
      {problems.original && (picked || typed) ? (
        <p className="field__help field__help--error">{problems.original}</p>
      ) : null}

      <div className="fields-row">
        <TextRow
          label={t('sales.note.number')}
          value={number}
          error={problems.number}
          help={!note && next.data && number === next.data.number ? t('sales.note.nextInSeries') : undefined}
          onChange={(v) => {
            setNumber(v.toUpperCase());
            setNumberTouched(true);
          }}
          maxLength={16}
        />
        <TextRow
          label={t('sales.note.date')}
          type="date"
          value={date}
          error={problems.date}
          onChange={setDate}
          max={indiaToday()}
        />
      </div>
      <SelectRow
        label={t('sales.note.why')}
        value={reason}
        onChange={(v) => setReason(v as NoteReason)}
        options={reasons.map((r) => ({ value: r, label: t(`sales.note.reasons.${r}`) }))}
      />

      {taxedLines.length > 1 ? (
        <SelectRow
          label={t('sales.note.item')}
          value={String(lineIndex)}
          onChange={(v) => {
            const index = Number(v);
            const line = taxedLines[index];
            setLineIndex(index);
            if (line) {
              setHsn(line.hsn);
              setHsnName(line.description ?? '');
              setRate(String(line.rate));
            }
          }}
          options={taxedLines.map((l, i) => ({
            value: String(i),
            label: `${l.description ?? l.hsn} · ${l.hsn} · ${l.rate}%`,
          }))}
        />
      ) : null}
      {!picked || !hsn ? (
        <div className="fields-row">
          <HsnField
            value={hsn}
            name={hsnName}
            date={(picked ?? typed)?.date || date}
            error={problems.hsn}
            onType={(code) => setHsn(code)}
            onPick={(result) => {
              setHsn(result.code);
              setHsnName(result.description);
              if (result.rate !== null && rate === '') setRate(String(result.rate));
            }}
          />
          <SelectRow
            label={t('sales.form.rate')}
            value={rate}
            error={problems.rate}
            placeholder="—"
            onChange={setRate}
            options={rates.map((r) => ({ value: String(r), label: `${r}%` }))}
          />
        </div>
      ) : (
        <p className="field__help">
          {t('sales.note.itemLine', { item: hsnName ? `${hsnName} · HSN ${hsn}` : `HSN ${hsn}`, rate })}
          {problems.rate ? <span className="field__help--error"> {problems.rate}</span> : null}
        </p>
      )}
      <div className="fields-row">
        {docType === 'credit' && reason === 'returned' ? (
          <TextRow
            label={t('sales.note.quantity')}
            value={quantity}
            onChange={setQuantity}
            inputMode="decimal"
            narrow
            help={t('sales.note.quantityHelp')}
          />
        ) : null}
        <TextRow
          label={t('sales.note.value')}
          value={value}
          error={problems.value}
          onChange={setValue}
          placeholder="10,000"
          help={t('sales.note.valueHelp')}
          inputMode="text"
        />
      </div>

      {tax && taxable !== null && rateNumber !== null ? (
        <div className="sale-preview" aria-live="polite">
          <span className="sale-preview__route">
            {t('sales.note.route', {
              how: inter ? t('sales.form.howInter') : t('sales.form.howSame'),
              from: state(seller),
              to: state(
                picked?.placeOfSupply ?? (typed?.consumer ? typed.state : (typed?.gstin.slice(0, 2) ?? seller)),
              ),
            })}
          </span>
          <span className="sale-preview__row">
            <span>
              {inter ? t('sales.form.igst', { rate: rateNumber }) : t('sales.form.cgstSgst', { half: rateNumber / 2 })}{' '}
              {t(docType === 'credit' ? 'sales.note.back' : 'sales.note.added')}
            </span>
            <span className="sale-preview__value">
              {inter ? money(tax.igst) : `${money(tax.cgst)} + ${money(tax.sgst)}`}
            </span>
          </span>
          <span className="sale-preview__row sale-preview__row--total">
            <span>{t('sales.note.noteValue')}</span>
            <span className="sale-preview__total">{money(taxable + tax.igst + tax.cgst + tax.sgst)}</span>
          </span>
          {kind ? (
            <span className="sale-preview__table">{t(`sales.note.goes.${kind === 'b2cs' ? 'b2cs' : kind}`)}</span>
          ) : null}
        </div>
      ) : null}
      {docType === 'credit' && kind === 'b2b' ? (
        <Callout tone="navy" icon="shield-check">
          {t('sales.note.ims', { buyer: picked?.buyer?.name ?? picked?.buyer?.gstin ?? typed?.gstin ?? '' })}
        </Callout>
      ) : null}
      {deadline ? <p className="field__help">{t('sales.note.deadline', { date: shortDate(deadline) })}</p> : null}
      {problems.other ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {problems.other}
        </Callout>
      ) : null}

      {note ? (
        confirmDelete ? (
          <div className="sale-form__delete" role="alert">
            <p>{t('sales.note.deleteConfirm', { number: note.numbers[0] })}</p>
            <div className="sale-form__delete-actions">
              <Button variant="danger" inline small onClick={remove} disabled={busy}>
                {t('sales.form.deleteYes')}
              </Button>
              <Button variant="ghost" inline small onClick={() => setConfirmDelete(false)}>
                {t('sales.form.deleteNo')}
              </Button>
            </div>
          </div>
        ) : (
          <button type="button" className="linkbtn sale-form__delete-link" onClick={() => setConfirmDelete(true)}>
            <Icon name="trash" size={16} />
            {t('sales.note.delete')}
          </button>
        )
      ) : null}
    </form>
  );
}
